import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, Download, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { Document, Page } from 'react-pdf';
import '../utils/pdfConfig';

const PDFViewer: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pdfLoadError, setPdfLoadError] = useState<boolean>(false);

  useEffect(() => {
    // Get the PDF URL from the query parameters
    const params = new URLSearchParams(location.search);
    const url = params.get('url');
    
    if (url) {
      try {
        const decodedUrl = decodeURIComponent(url);
        
        // Validate URL format
        if (!decodedUrl.startsWith('http')) {
          setError('Invalid PDF URL format');
          setIsLoading(false);
          return;
        }
        
        // Check if URL is for a PDF (basic check)
        const isPdf = decodedUrl.endsWith('.pdf');
        if (!isPdf) {
          setError('URL does not appear to be a PDF');
          setIsLoading(false);
          return;
        }
        
        setPdfUrl(decodedUrl);
        setIsLoading(false);
      } catch (e) {
        setError('Error processing the PDF URL');
        setIsLoading(false);
      }
    } else {
      setError('No PDF URL provided');
      setIsLoading(false);
    }
  }, [location.search]);

  const handleClose = () => {
    navigate(-1); // Go back to the previous page
  };

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    setPdfLoadError(false);
  }

  function onDocumentLoadError() {
    console.error('Failed to load PDF');
    setPdfLoadError(true);
  }

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (numPages && currentPage < numPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-700 dark:text-gray-300">Loading PDF...</p>
        </div>
      </div>
    );
  }

  if (error || pdfLoadError) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100 dark:bg-gray-900">
        <div className="text-center p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <p className="text-red-500">{error || 'Failed to load PDF. The file might be corrupted or inaccessible.'}</p>
          <button 
            onClick={handleClose}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100 dark:bg-gray-900">
      {/* Header */}
      <div className="flex justify-between items-center p-4 bg-white dark:bg-gray-800 shadow-md">
        <h1 className="text-xl font-semibold text-gray-800 dark:text-white">PDF Viewer</h1>
        <button 
          onClick={handleClose}
          className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        >
          <X className="w-6 h-6 text-gray-600 dark:text-gray-300" />
        </button>
      </div>
      
      {/* PDF Container */}
      <div className="flex-1 flex items-center justify-center p-4 overflow-auto">
        <div className="max-w-full max-h-full">
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            className="max-w-full"
          >
            <Page 
              pageNumber={currentPage} 
              renderTextLayer={false}
              renderAnnotationLayer={false}
              className="shadow-lg rounded-lg"
            />
          </Document>
        </div>
      </div>
      
      {/* Page Controls */}
      {numPages && numPages > 1 && (
        <div className="flex justify-center items-center p-2 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
          <button 
            onClick={goToPreviousPage} 
            disabled={currentPage <= 1}
            className={`p-2 rounded-full ${currentPage <= 1 ? 'text-gray-400 dark:text-gray-600' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="mx-4 text-gray-700 dark:text-gray-300">
            Page {currentPage} of {numPages}
          </span>
          <button 
            onClick={goToNextPage} 
            disabled={currentPage >= (numPages || 1)}
            className={`p-2 rounded-full ${currentPage >= (numPages || 1) ? 'text-gray-400 dark:text-gray-600' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}
      
      {/* Footer */}
      <div className="p-4 bg-white dark:bg-gray-800 shadow-md">
        <div className="flex justify-between items-center">
          <div className="flex gap-2">
            <button 
              onClick={handleClose}
              className="px-4 py-2 flex items-center gap-2 bg-gray-300 dark:bg-gray-700 text-gray-800 dark:text-white rounded hover:bg-gray-400 dark:hover:bg-gray-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <a 
              href={pdfUrl} 
              download 
              className="px-4 py-2 flex items-center gap-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              <Download className="w-4 h-4" />
              Download PDF
            </a>
          </div>
          <div>
            {pdfUrl && (
              <span className="text-xs text-gray-500 dark:text-gray-400 italic">
                PDF from: {new URL(pdfUrl).hostname}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;
